import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { BiSolidError } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { FiEdit, FiEye } from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import { MdOutlineEdit } from "react-icons/md";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import ActionMenu from "../../../component/ActionMenu";
import { server } from "../../../App";
import {
  getStatusBadge,
  getStatusText,
} from "../../../component/admin/VehiclesTable";
import CreateModal from "../../../component/Model/CreateModel";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import {
  useGetRegulatoryComplianceQuery,
  useUpdateRegulatoryComplianceMutation,
} from "../../../store/api/vendor/regulatoryComplianceSlice";
import {
  formatDateToLocal,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";
import "./v.scss";

const initialActions = {
  search: "",
  status: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};

// STATE FOR ADMION TO UPDATE COMPLIANCE STATUS
const initialState = {
  compliance_status: "",
  description: "",
};

const RegulatoryCompliances = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [show, setShow] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [id, setId] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);
  const [regulatoryCompliance, setRegulatoryCompliance] = useState([]);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  const { user } = useSelector((state) => state.UserReducer);
  const { role, slug } = useParams();
  const isAdmin = user?.role === "super_admin";

  // For admin, set role filter immediately from slug to avoid double API call
  useEffect(() => {
    if (isAdmin) {
      const adminRole =
        slug === "school-vehicle-documents" ? "school" : "vendor";
      setAction((prev) => {
        if (prev.role === adminRole) return prev; // already set, no re-render
        return { ...prev, role: adminRole };
      });
    }
  }, [slug, isAdmin]);

  const adminRole = isAdmin
    ? slug === "school-vehicle-documents"
      ? "school"
      : "vendor"
    : undefined;

  const { data, isSuccess, isLoading, isError, error, isFetching } =
    useGetRegulatoryComplianceQuery({
      page: pagination.page,
      limit: pagination.limit,
      ...action,
      ...(adminRole && !action.role ? { role: adminRole } : {}),
    });

  // UPDATE VEHICLE REGULATORY COMPLIANCE STATUS API (FOR ADMIN)
  const [updateRegulatoryCompliance, { isLoading: isUpdateLoading }] =
    useUpdateRegulatoryComplianceMutation();

  useEffect(() => {
    if (isSuccess && data?.data) {
      setRegulatoryCompliance(data.data.compliances);
      setPagination(data.data.pagination);
    }
  }, [data, isSuccess]);

  // Delete modal show function
  const handleShowDelete = (id) => {
    setShowDelete(true);
    setId(id);
  };

  // INPUT CHNAGE
  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (value) {
      setErrors({});
    }
  };

  // Detail modal show function
  const handleShowDetail = (record) => {
    setSelectedRecord(record);
    setShowDetail(true);
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: search.trim() }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      // setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleCloseDelete = () => setShowDelete(false);

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedRecord(null);
  };

  const handleClose = () => {
    setShow(false);
    setSelectedRecord(null);
    setShowCreate(false);
  };

  // filters chnages
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAction((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ limit: 10, page: 1 }));
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Get file type icon
  const getFileIcon = (fileName) => {
    if (!fileName) return "bi-file-earmark";

    const extension = fileName.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
      return "bi-file-earmark-image";
    } else if (extension === "pdf") {
      return "bi-file-earmark-pdf";
    } else if (["doc", "docx"].includes(extension)) {
      return "bi-file-earmark-word";
    } else if (["xls", "xlsx"].includes(extension)) {
      return "bi-file-earmark-excel";
    } else {
      return "bi-file-earmark";
    }
  };

  // Get file name from URL
  const getFileName = (url) => {
    if (!url) return "No file";
    return url.split("/").pop();
  };

  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
    setSearch("");
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate vehicle selection
    if (!formData.compliance_status) {
      newErrors.compliance_status = "Compliance Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApproveRequest = async () => {
    if (!validateForm()) return;
    toast.dismiss();
    try {
      let response = await updateRegulatoryCompliance({
        data: { ...formData, vehicle_id: selectedRecord?.vehicle_id },
        id: selectedRecord.id,
      }).unwrap();
      setFormData(initialState);
      setSelectedRecord(null);
      setShow(false);
      setShowCreate(true);
    } catch (err) {
      handleApiError(err);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")} ${t("by")} ${t("license")} ${t(
                "number",
              )}`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              name="status"
              value={action.status}
              onChange={handleChange}
            >
              <option value="">
                {t("select")} {t("status")}
              </option>
              <option value="pending">{t("pending")}</option>
              <option value="verified">{t("verified")}</option>
              <option value="rejected">{t("rejected")}</option>
            </select>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            {!isAdmin && (
              <NavLink
                to="add"
                className="page-add-btn d-flex align-items-center justify-content-center gap-2"
              >
                <FaPlus /> {t("add")} {t("regulatory")} {t("compliance")}
              </NavLink>
            )}
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th>
                {t("vehicle")} {t("license")} {t("number")}
              </th>
              <th>
                {t("registration")} {t("number")}
              </th>
              <th>
                {t("vehicle")} {t("type")}
              </th>
              <th>{t("status")}</th>
              <th>{t("owner")}</th>
              <th>{t("created")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading length={8} />
            ) : regulatoryCompliance.length > 0 ? (
              regulatoryCompliance.map((item, i) => (
                <tr key={i}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">
                    {item.vehicle?.license_number || "N/A"}
                  </td>
                  <td className="text-nowrap">
                    {item.vehicle?.registration_number || "N/A"}
                  </td>
                  <td className="text-nowrap">
                    {item.vehicle?.vehicle_type || "N/A"}
                  </td>
                  <td>
                    <span className={getStatusBadge(item?.compliance_status)}>
                      {getStatusText(item?.compliance_status)}
                    </span>

                    {/* {getStatusBadge(item.compliance_status)} */}
                  </td>

                  <td className="text-nowrap">
                    <div className="small">
                      <div>{item.owner?.name}</div>
                      <div className="text-muted">{item.owner?.role}</div>
                    </div>
                  </td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.created_at)}
                  </td>
                  <td>
                    <ActionMenu
                      actions={[
                        {
                          label: `${t("view")} ${t("details")}`,
                          icon: <FiEye size={15} />,
                          onClick: () => handleShowDetail(item),
                        },
                        {
                          label: t("edit"),
                          icon: <MdOutlineEdit size={16} />,
                          onClick: () =>
                            navigate(`/regulatory-compliance/edit/${item.id}`),
                          hidden: isAdmin,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="7">
                  <div>
                    <span>{t("noComplianceRecords")}</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDelete}>
        <Button className="closePopup" onClick={handleCloseDelete}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">{t("delete-modal")}</div>
            <div className="text-center">
              <Button
                className="smallBtn cancel me-3"
                onClick={handleCloseDelete}
              >
                {t("btns.cancel")}
              </Button>
              <Button className="smallBtn auto">{t("btns.delete")}</Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        show={showDetail}
        onHide={handleCloseDetail}
        size="xl"
        centered
        className="big"
      >
        <Modal.Header className="border-0" closeButton>
          <Modal.Title className="h5">
            {t("compliance")} {t("record")} {t("details")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "calc(80vh - 120px)",
            overflowY: "auto",
          }}
        >
          {selectedRecord && (
            <div className="p-2">
              {/* Basic Information */}
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  {t("basic")} {t("information")}
                </h6>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong className="text-muted d-block mb-1">
                      {t("vehicle")} {t("license")} {t("number")}:
                    </strong>
                    <span className="h6">
                      {selectedRecord.vehicle?.license_number || "N/A"}
                    </span>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong className="text-muted d-block mb-1">
                      {t("registration")} {t("number")}:
                    </strong>
                    <span>
                      {selectedRecord.vehicle?.registration_number || "N/A"}
                    </span>
                  </div>
                  {/* status */}
                  <div className="col-md-6 mb-3">
                    <strong className="text-muted d-block mb-1">
                      {t("compliance")} {t("status")}:
                    </strong>
                    <span
                      className={getStatusBadge(
                        selectedRecord?.compliance_status,
                      )}
                    >
                      {getStatusText(selectedRecord?.compliance_status)}
                    </span>
                  </div>
                  {(selectedRecord?.compliance_status === "verified" ||
                    selectedRecord?.compliance_status === "rejected") && (
                    <>
                      {/* verify by admin */}
                      <div className="col-md-6 mb-3">
                        <strong className="text-muted d-block mb-1">
                          {getStatusText(selectedRecord?.compliance_status)}{" "}
                          {t("by")}:
                        </strong>
                        <span
                          className={getStatusBadge(
                            selectedRecord?.compliance_status,
                          )}
                        >
                          {getStatusText("admin")}
                        </span>
                      </div>
                      {/* admin comment */}
                      <div className="col-md-6 mb-3">
                        <strong className="text-muted d-block mb-1">
                          {t("admin")} {t("comment")}:
                        </strong>
                        {selectedRecord?.description}
                      </div>
                    </>
                  )}

                  <div className="col-md-6 mb-3">
                    <strong className="text-muted d-block mb-1">
                      {t("owner")}:
                    </strong>
                    <div>
                      <div>{selectedRecord.owner?.name}</div>
                      <small className="text-muted">
                        {selectedRecord.owner?.role} -{" "}
                        {selectedRecord.owner?.email}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Section */}
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">{t("documents")}</h6>
                <div className="row g-3">
                  {[
                    {
                      key: "registration_document",
                      expiry: "registration_expiry",
                      icon: "bi-file-earmark-text",
                      title: `${t("registration")} ${t("document")}`,
                      subtitle: `${t("vehicle")} ${t("registration")} ${t("certificate")}`,
                    },
                    {
                      key: "license_document",
                      expiry: "license_expiry",
                      icon: "bi-file-earmark-check",
                      title: `${t("license")} ${t("document")}`,
                      subtitle: `${t("driver")} ${t("license")}`,
                    },
                    {
                      key: "insurance_document",
                      expiry: "insurance_expiry",
                      icon: "bi-shield-check",
                      title: `${t("insurance")} ${t("document")}`,
                      subtitle: `${t("vehicle")} ${t("insurance")} ${t("certificate")}`,
                    },
                    {
                      key: "safety_check_document",
                      expiry: "safety_check_expiry",
                      icon: "bi-clipboard-check",
                      title: `${t("safety")} ${t("check")} ${t("document")}`,
                      subtitle: `${t("vehicle")} ${t("safety")} ${t("inspection")}`,
                    },
                    {
                      key: "permit_document",
                      expiry: "permit_expiry",
                      icon: "bi-file-earmark-ruled",
                      title: "DOT #/Permit Document",
                      subtitle: "Department of Transportation Permit",
                    },
                  ].map(({ key, expiry, icon, title, subtitle }) => (
                    <div className="col-md-6" key={key}>
                      <div className="card h-100 border">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-warning bg-opacity-10 p-2 rounded me-3">
                              <i className={`bi ${icon} text-warning fs-4`}></i>
                            </div>
                            <div>
                              <h6 className="card-title mb-0">{title}</h6>
                              <small className="text-muted">{subtitle}</small>
                            </div>
                          </div>

                          {selectedRecord[key] ? (
                            <>
                              {selectedRecord[expiry] && (
                                <div className="mb-2 d-flex align-items-center justify-content-between">
                                  <small className="text-muted d-block mb-1">
                                    Expiry Date:
                                  </small>
                                  <span className="badge bg-light text-dark border">
                                    <i className="bi bi-calendar3 me-1"></i>
                                    {formatDateToLocal(selectedRecord[expiry])}
                                  </span>
                                </div>
                              )}

                              <div className="mb-3">
                                <small className="text-muted d-block mb-1">
                                  {t("file")}:
                                </small>
                                <div className="d-flex align-items-center gap-2">
                                  <i
                                    className={`bi ${getFileIcon(selectedRecord[key])}`}
                                  ></i>
                                  <span className="text-truncate">
                                    {getFileName(selectedRecord[key])}
                                  </span>
                                </div>
                              </div>

                              <a
                                href={`${server}${selectedRecord[key]}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center gap-2 mt-2"
                              >
                                <i className="bi bi-eye"></i>
                                {t("view")} {t("document")}
                              </a>
                            </>
                          ) : (
                            <div className="text-center py-3">
                              <i className="bi bi-file-x text-muted fs-1 mb-2 d-block"></i>
                              <span className="text-muted">
                                {t("no")} {t("document")} {t("uploaded")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Information */}
              <div className="mt-4 pt-3 border-top">
                <h6 className="border-bottom pb-2 mb-3">
                  {t("additional")} {t("information")}
                </h6>
                <div className="row">
                  {selectedRecord.description && (
                    <div className="col-12 mb-3">
                      <strong className="text-muted d-block mb-1">
                        {t("description")}:
                      </strong>
                      <div className="p-3 bg-light rounded">
                        {selectedRecord.description}
                      </div>
                    </div>
                  )}
                  <div className="col-md-6">
                    <strong className="text-muted d-block mb-1">
                      {t("created")}:
                    </strong>
                    <span>{formatDateTime(selectedRecord.created_at)}</span>
                  </div>
                  <div className="col-md-6">
                    <strong className="text-muted d-block mb-1">
                      {t("last")} {t("updated")}:
                    </strong>
                    <span>{formatDateTime(selectedRecord.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={handleCloseDetail}>
            {t("close")}
          </Button>
          {selectedRecord?.compliance_status === "pending" && isAdmin && (
            <Button
              variant="primary"
              onClick={() => {
                setShow(true);
                setShowDetail(false);
              }}
            >
              {t("Verify")}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header className="border-0" closeButton>
          <Modal.Title className="h5">
            {t("update")} {t("compliance")} {t("status")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="p-2">
            {/* Status Selection */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                {t("status")} <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="compliance_status"
                value={formData.compliance_status}
                onChange={handleChangeForm}
              >
                <option value="">
                  {t("select")} {t("status")}
                </option>
                <option value="verified">{t("verified")}</option>
                <option value="rejected">{t("rejected")}</option>
              </select>
              {errors?.compliance_status && (
                <span className="text-danger small mt-1 d-block">
                  {errors?.compliance_status}
                </span>
              )}
            </div>

            {/* Comment Section */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                {action.status === "rejected" ? "Rejection Reason" : "Comments"}
                {action.status === "rejected" && (
                  <span className="text-danger ms-1">*</span>
                )}
              </label>
              <textarea
                className="form-control"
                rows="3"
                placeholder={
                  action.status === "rejected"
                    ? t("rejectionReason")
                    : t("addComments")
                }
                value={formData.description}
                name="description"
                onChange={handleChangeForm}
              ></textarea>
            </div>

            {/* Status Info Box */}
            {action.status === "verified" && (
              <div className="alert alert-success mb-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <div>
                    <strong>{t("verifiedStatus")}</strong>{" "}
                    {t("verifiedStatusDescription")}
                  </div>
                </div>
              </div>
            )}

            {action.status === "rejected" && (
              <div className="alert alert-danger mb-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-x-circle-fill me-2"></i>
                  <div>
                    <strong>{t("rejectedStatus")}</strong>{" "}
                    {t("rejectedStatusDescription")}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0">
          <div className="w-100 d-flex justify-content-end gap-2">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              className="px-4"
            >
              {t("cancel")}
            </Button>
            <Button
              variant={
                formData.compliance_status === "rejected" ? "danger" : "primary"
              }
              onClick={handleApproveRequest}
              disabled={
                isUpdateLoading ||
                selectedRecord?.compliance_status === "rejected"
              }
              className="px-4"
            >
              {isUpdateLoading ? (
                <>
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                </>
              ) : formData.compliance_status === "verified" ? (
                "Approve"
              ) : formData.compliance_status === "rejected" ? (
                "Reject"
              ) : (
                "Verify"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreate} centered>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>

        <CreateModal
          title={t("complianceUpdateSuccessTitle")}
          text={t("complianceUpdateSuccessText")}
          onClose={handleClose}
        />
      </Modal>

      <style jsx>
        {`
          /* Modal styling */
          .big .modal-dialog {
            max-width: 900px;
          }
          /* Card hover effects */
          .card {
            transition:
              transform 0.2s,
              box-shadow 0.2s;
          }
          .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          /* File icon colors */
          .bi-file-earmark-pdf {
            color: #dc3545;
          }

          .bi-file-earmark-image {
            color: #198754;
          }

          .bi-file-earmark-word {
            color: #0d6efd;
          }
          .bi-file-earmark-excel {
            color: #198754;
          }
        `}
      </style>
    </>
  );
};

export default RegulatoryCompliances;
